// Custom domains for Pages, the browser's side: the same pure rules as the server
// (nl_aos_replit_ready/nl_aos_clean/utils/ownHosts.js and utils/pageDomains.js), so the
// Domains screen can check what someone typed before sending it, and the share links can
// pick an origin. test/pages-domains.test.js loads this file in Node and compares every
// rule with the server's copy, so keep it import-free with plain (erasable) types only.

export type DomainStatus = 'pending' | 'active' | 'error';

export interface DnsRecord { type: 'A' | 'CNAME' | 'TXT'; name: string; value: string }

/** Vercel's GET /v6/domains/{d}/config, the parts the records come from. */
export interface VercelDomainConfig {
  misconfigured?: boolean;
  recommendedIPv4?: { rank?: number; value?: string[] | string }[];
  recommendedCNAME?: { rank?: number; value?: string }[];
}

/** One entry of the project domain's verification list (a TXT record Vercel wants). */
export interface VercelVerification { type?: string; domain?: string; value?: string; reason?: string }

export const FALLBACK_A = '76.76.21.21';
export const FALLBACK_CNAME = 'cname.vercel-dns.com';

// ── Hosts ────────────────────────────────────────────────────

/** A Host header or hostname as one lowercase name: first of a list, no port, no [brackets], no trailing dot. */
export function normalizeHost(value: string | null | undefined): string {
  let h = String(value ?? '').split(',')[0].trim().toLowerCase();
  if (!h) return '';
  if (h.startsWith('[')) {
    const end = h.indexOf(']');
    h = end > 0 ? h.slice(1, end) : h.slice(1);
  } else if ((h.match(/:/g) || []).length === 1) {
    h = h.replace(/:\d*$/, '');
  }
  return h.replace(/\.+$/, '');
}

/**
 * Hosts that are never a client's domain: no host, localhost and loopback, bare IP
 * addresses, Vercel addresses (production and every preview) and dot-less names.
 */
export function isAlwaysOwnHost(host: string | null | undefined): boolean {
  const h = normalizeHost(host);
  if (!h) return true;
  if (h === 'localhost' || h.endsWith('.localhost')) return true;
  if (h === '127.0.0.1' || h === '::1' || h === '0.0.0.0') return true;
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(h)) return true;
  if (h === 'vercel.app' || h.endsWith('.vercel.app')) return true;
  return !h.includes('.');
}

/** A client's custom domain: not always ours and not one of `ownHosts` (e.g. the app host the Domains API names). */
export function isCustomHost(host: string | null | undefined, ownHosts: Iterable<string> = []): boolean {
  const h = normalizeHost(host);
  if (isAlwaysOwnHost(h)) return false;
  for (const own of ownHosts) if (normalizeHost(own) === h) return false;
  return true;
}

// ── Domains and DNS records ──────────────────────────────────

/** What someone typed as a domain, as the bare lowercase host: no scheme, path, login, port or trailing dot. */
export function normalizeDomain(input: string | null | undefined): string {
  return String(input ?? '').trim().toLowerCase()
    .replace(/^[a-z][a-z0-9+.-]*:\/\//, '')
    .replace(/[/?#].*$/, '')
    .replace(/^[^@]*@/, '')
    .replace(/:\d*$/, '')
    .replace(/\.+$/, '');
}

const DOMAIN_RE = /^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+(?:[a-z]{2,63}|xn--[a-z0-9-]{2,59})$/;

/** A real domain name: labels of letters, digits and dashes, at least one dot, a real top-level part. */
export function isValidDomain(input: string | null | undefined): boolean {
  return DOMAIN_RE.test(normalizeDomain(input));
}

/** The registrable part when Vercel did not say: the last two labels. Vercel's own apexName knows acme.co.uk. */
export function apexGuess(domain: string): string {
  return normalizeDomain(domain).split('.').slice(-2).join('.');
}

/** Is `domain` its own apex (an A record) rather than a subdomain (a CNAME)? */
export function isApex(domain: string, apex?: string | null): boolean {
  const d = normalizeDomain(domain);
  return d === (normalizeDomain(apex) || apexGuess(d));
}

/** The name to type at the DNS provider: '@' for the apex itself, else the part before the apex. */
export function recordName(fqdn: string, apex: string): string {
  const f = normalizeDomain(fqdn);
  const a = normalizeDomain(apex);
  if (!f || f === a) return '@';
  return a && f.endsWith(`.${a}`) ? f.slice(0, -(a.length + 1)) : f;
}

/** Vercel's first-ranked recommendation (the lowest rank), or null. */
export function bestRanked<T extends { rank?: number }>(list: T[] | null | undefined): T | null {
  if (!Array.isArray(list)) return null;
  const items = list.filter((x) => x && typeof x === 'object');
  if (!items.length) return null;
  const rank = (x: T) => (Number.isFinite(Number(x.rank)) ? Number(x.rank) : Number.MAX_SAFE_INTEGER);
  return items.reduce((best, x) => (rank(x) < rank(best) ? x : best));
}

const IPV4_RE = /^\d{1,3}(\.\d{1,3}){3}$/;

/**
 * The DNS records to show for a domain: one A record per first-ranked recommended IPv4
 * for an apex, one CNAME to the first-ranked recommended target for a subdomain, then
 * every TXT record Vercel wants for proving ownership. Falls back to Vercel's standard
 * 76.76.21.21 and cname.vercel-dns.com without Vercel's answer.
 */
export function dnsRecordsFor(input: { domain: string; apex?: string | null; config?: VercelDomainConfig | null; verification?: VercelVerification[] | null }): DnsRecord[] {
  const d = normalizeDomain(input.domain);
  const a = normalizeDomain(input.apex) || apexGuess(d);
  const out: DnsRecord[] = [];
  if (d === a) {
    const best = bestRanked(input.config?.recommendedIPv4);
    const raw: unknown[] = best ? (Array.isArray(best.value) ? best.value : [best.value]) : [];
    const ips = raw.map((v) => String(v ?? '').trim()).filter((v) => IPV4_RE.test(v));
    for (const ip of ips.length ? ips : [FALLBACK_A]) out.push({ type: 'A', name: '@', value: ip });
  } else {
    const best = bestRanked(input.config?.recommendedCNAME);
    const target = normalizeDomain(best && typeof best.value === 'string' ? best.value : '');
    out.push({ type: 'CNAME', name: recordName(d, a), value: isValidDomain(target) ? target : FALLBACK_CNAME });
  }
  for (const v of Array.isArray(input.verification) ? input.verification : []) {
    if (!v || String(v.type || '').toUpperCase() !== 'TXT' || !v.value) continue;
    out.push({ type: 'TXT', name: recordName(v.domain || a, a), value: String(v.value) });
  }
  return out;
}

// ── Share links ──────────────────────────────────────────────

/** Every origin public links can use: the app origin (fallback) first, then each active custom domain. */
export function publicOriginOptions(domains: { domain: string; status: string }[], fallback: string): string[] {
  const actives = domains.filter((d) => d.status === 'active').map((d) => `https://${d.domain}`);
  return [fallback, ...actives.filter((o) => o !== fallback)];
}

/**
 * The origin for public links. `preferred` (the user's saved choice) wins while it is
 * still an option, so a removed domain never ends up in a link; else the first active
 * custom domain, else the fallback (the app origin).
 */
export function pickPublicOrigin(domains: { domain: string; status: string }[], fallback: string, preferred?: string | null): string {
  if (preferred && publicOriginOptions(domains, fallback).includes(preferred)) return preferred;
  const active = domains.find((d) => d.status === 'active');
  return active ? `https://${active.domain}` : fallback;
}
